// Einheiten-Tab (README §5.4, Screens 20–22) – reine Logik:
// Filter, Kennzahlen, Häuser-Übersicht und das Formular „Neue Einheit“.

import type { EinheitArt, EinheitUebersicht } from './objektDetail';
import { parseDezimal } from './stammdaten';

export type EinheitenFilter = 'alle' | 'vermietet' | 'frei' | 'gewerbe';

/** `zusatz`: weiterer Suchtext je Einheit, z. B. der Eigentümer (#514). */
export function filtereEinheiten(liste: readonly EinheitUebersicht[], filter: EinheitenFilter, suche = '', zusatz?: ReadonlyMap<string, string>): EinheitUebersicht[] {
  const q = suche.trim().toLowerCase();
  return liste.filter(e =>
    (filter === 'alle' || (filter === 'vermietet' ? e.vermietet : filter === 'frei' ? !e.vermietet : e.art === 'gewerbe' || e.art === 'stellplatz'))
    && (!q || `${e.bezeichnung} ${e.mieter.join(' ')} ${zusatz?.get(e.id) ?? ''}`.toLowerCase().includes(q)));
}

export function filterAnzahlen(liste: readonly EinheitUebersicht[]): Record<EinheitenFilter, number> {
  return {
    alle: liste.length,
    vermietet: liste.filter(e => e.vermietet).length,
    frei: liste.filter(e => !e.vermietet).length,
    gewerbe: liste.filter(e => e.art === 'gewerbe' || e.art === 'stellplatz').length,
  };
}

export const OHNE_HAUS = '__ohne';

export interface HausZusammenfassung { id: string; bezeichnung: string; adresse: string; anzahl: number; frei: number; summe: number }

/** Je Haus: Einheiten, freie Einheiten, Gesamtmiete; „Ohne Haus“ nur, wenn es solche Einheiten gibt. */
export function haeuserUebersicht(
  haeuser: readonly { id: string; bezeichnung: string | null; strasse: string | null; hausnummer: string | null }[],
  liste: readonly EinheitUebersicht[],
): HausZusammenfassung[] {
  const zeile = (id: string, bezeichnung: string, adresse: string, einheiten: readonly EinheitUebersicht[]): HausZusammenfassung => ({
    id, bezeichnung, adresse,
    anzahl: einheiten.length,
    frei: einheiten.filter(e => !e.vermietet).length,
    summe: Math.round(einheiten.reduce((s, e) => s + e.gesamt, 0) * 100) / 100,
  });
  const aus = haeuser.map(h => zeile(h.id, (h.bezeichnung ?? '').trim() || 'Haus', [h.strasse, h.hausnummer].filter(Boolean).join(' '), liste.filter(e => e.hausId === h.id)));
  const ohne = liste.filter(e => !e.hausId || !haeuser.some(h => h.id === e.hausId));
  if (ohne.length) aus.push(zeile(OHNE_HAUS, 'Ohne Haus', 'Einheiten ohne Hauszuordnung', ohne));
  return aus;
}

// ── Neue Einheit ─────────────────────────────────────────────────────

export const NEUE_ARTEN = [
  { key: 'wohnung', label: 'Wohnung' },
  { key: 'garage', label: 'Garage' },
  { key: 'stellplatz', label: 'Stellplatz' },
  { key: 'gewerbe', label: 'Gewerbe' },
] as const;
export type NeueArt = typeof NEUE_ARTEN[number]['key'];

export const LAGEN = ['UG', 'EG', '1.OG', '2.OG', '3.OG', 'DG', 'Außen'] as const;
export const ZIMMER = ['1', '2', '3', '4', '5+'] as const;

export interface NeueEinheitEntwurf {
  art: NeueArt;
  lage: string;
  bezeichnung: string;
  flaeche: string;
  zimmer: string;
  hausId: string | null;
  vermietet: boolean;
  anrede: string;
  vorname: string;
  nachname: string;
  mietbeginn: string;
  kalt: string;
  nk: string;
  hk: string;
}

export function leererEntwurf(hausId: string | null): NeueEinheitEntwurf {
  return { art: 'wohnung', lage: 'EG', bezeichnung: '', flaeche: '', zimmer: '2', hausId, vermietet: false, anrede: 'Herr', vorname: '', nachname: '', mietbeginn: '', kalt: '', nk: '', hk: '' };
}

/** Wohnungen und Gewerbe brauchen eine Fläche, Garagen/Stellplätze nicht. Zimmer nur bei Wohnungen. */
export const brauchtFlaeche = (art: NeueArt): boolean => art === 'wohnung' || art === 'gewerbe';
export const hatZimmer = (art: NeueArt): boolean => art === 'wohnung';

/** Vorschlag für die Bezeichnung: laufende Nummer + Lage, z. B. „05 EG“ bzw. „005 Garage“. */
export function bezeichnungsVorschlag(anzahl: number, art: NeueArt, lage: string): string {
  if (art === 'garage' || art === 'stellplatz') return `${String(anzahl + 1).padStart(3, '0')} ${art === 'garage' ? 'Garage' : 'Stellplatz'}`;
  return `${String(anzahl + 1).padStart(2, '0')} ${lage === 'Außen' ? '' : lage}`.trim();
}

export interface Pruefung { schritte: [boolean, boolean, boolean]; offen: string[] }

export function pruefeEntwurf(e: NeueEinheitEntwurf, bestehendeBezeichnungen: readonly string[], vorschlag: string): Pruefung {
  const offen: string[] = [];
  const bez = (e.bezeichnung.trim() || vorschlag).toLowerCase();
  const doppelt = bestehendeBezeichnungen.some(b => b.trim().toLowerCase() === bez);
  const flaecheOk = !brauchtFlaeche(e.art) ? (e.flaeche.trim() === '' || parseDezimal(e.flaeche) !== null) : (parseDezimal(e.flaeche) ?? 0) > 0;
  if (doppelt) offen.push('eindeutige Bezeichnung');
  if (!flaecheOk) offen.push(brauchtFlaeche(e.art) ? (e.art === 'wohnung' ? 'Wohnfläche' : 'Fläche') : 'gültige Fläche');
  const schritt2 = !doppelt && flaecheOk;
  let schritt3 = true;
  if (e.vermietet) {
    const name = e.vorname.trim() && e.nachname.trim();
    const kalt = (parseDezimal(e.kalt) ?? 0) > 0;
    if (!name) offen.push('Mieter');
    if (!kalt) offen.push('Kaltmiete');
    schritt3 = !!name && kalt;
  }
  return { schritte: [true, schritt2, schritt3], offen };
}

const TYP: Record<NeueArt, string> = { wohnung: 'Wohnung', garage: 'Stellplatz', stellplatz: 'Stellplatz', gewerbe: 'Gewerbe' };

/** Zeile für objekt_einheiten (Werte wie im bisherigen Einheiten-Formular). */
export function einheitEinfuegen(e: NeueEinheitEntwurf, objektId: string, vorschlag: string): Record<string, string | number | null> {
  const zimmer = hatZimmer(e.art) ? (e.zimmer === '5+' ? 5 : Number(e.zimmer) || null) : null;
  return {
    objekt_id: objektId,
    haus_id: e.hausId && e.hausId !== OHNE_HAUS ? e.hausId : null,
    bezeichnung: e.bezeichnung.trim() || vorschlag,
    typ: TYP[e.art],
    wohnflaeche: parseDezimal(e.flaeche),
    zimmer,
    etage: e.lage,
    status: e.vermietet ? 'vermietet' : 'leerstand',
  };
}

/** Zeile für mieter, wenn die Einheit direkt vermietet angelegt wird. */
export function mieterEinfuegen(e: NeueEinheitEntwurf, objektId: string, einheitId: string, bezeichnung: string): Record<string, string | number | null> {
  return {
    anrede: e.anrede || null,
    vorname: e.vorname.trim(),
    nachname: e.nachname.trim(),
    objekt_id: objektId,
    einheit_id: einheitId,
    einheit: bezeichnung,
    mietbeginn: e.mietbeginn || null,
    kaltmiete: parseDezimal(e.kalt) ?? 0,
    nebenkosten_vorauszahlung: parseDezimal(e.nk) ?? 0,
    heizkosten: parseDezimal(e.hk) ?? 0,
    status: 'aktiv',
  };
}

export function gesamtAusEntwurf(e: NeueEinheitEntwurf): number {
  return Math.round(((parseDezimal(e.kalt) ?? 0) + (parseDezimal(e.nk) ?? 0) + (parseDezimal(e.hk) ?? 0)) * 100) / 100;
}

export function artVonNeu(a: NeueArt): EinheitArt {
  return a === 'garage' ? 'stellplatz' : a;
}
